import React, { useState, useEffect } from 'react';
import { Box, useTheme, Typography, Container, IconButton, Fade } from '@mui/material';
import { Feedback, People, KeyboardArrowUp } from '@mui/icons-material';
import Footer from '../components/LandingPage/Footer';
import DrinkShowcase from '../components/LandingPage/DrinkShowcase';
import FAQSection from '../components/LandingPage/FAQSection';
import TestimonialsSection from '../components/LandingPage/TestimonialSection';
import CTASection from '../components/LandingPage/CTASection';
import TextSection from '../components/LandingPage/TextSection';
import HeroSection from '../components/LandingPage/HeroSection'; 
import StateBoxesSection from '../components/LandingPage/StateBoxesSection';
import FeedbackModal from '../components/LandingPage/FeedbackModal';
import CommunityChatModal from '../components/Chat/ChatModal';
import LoginRequiredModal from '../components/community/LoginRequiredModal';
import ChatSection from '../components/LandingPage/ChatSection';
import Partners from '../components/LandingPage/Partners'; 
import CommunitySection from '../components/LandingPage/CommunitySection'; 
import ComingSoonSection from '../components/LandingPage/ComingSoonSection'; 
import { useAuth } from '../context/AuthContext'; 
import './LandingPage.css'; 
import { LandingSkeleton } from '../components/skeletons';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Use your AuthContext
  const { user, token, isLoading } = useAuth();
  
  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;
  
  // Page Background Colors
  const bgColor = isDark ? '#000000' : '#FAFAFA';
  const cardTitleColor = isDark ? 'white' : '#051814';
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Show button when scrolled more than 100vh (100% of viewport height)
      setShowScrollTop(scrolled > viewportHeight);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Check on initial load
    handleScroll();

    // Cleanup
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleChatClick = () => {
    // Don't do anything if still loading auth state
    if (isLoading) return;
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Show login required modal
      setLoginModalOpen(true);
    } else {
      // User is authenticated, show chat modal
      setChatOpen(true);
    }
  };

  const handleFeedbackClick = () => {
    // Don't do anything if still loading auth state
    if (isLoading) return;
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Show login required modal
      setLoginModalOpen(true);
    } else {
      // User is authenticated, show feedback modal
      setFeedbackOpen(true);
    }
  };

  // Show loading state if authentication is still being checked
if (isLoading) {
  return <LandingSkeleton />;
}

  return (
    <Box sx={{ 
      bgcolor: bgColor,
      color: isDark ? 'white' : '#000',
      overflowX: 'hidden',
      minHeight: '100vh',
      position: 'relative',
    }}>
      
      {/* Floating Chat Button */}
      <IconButton
        onClick={handleChatClick}
        disabled={isLoading}
        sx={{
          position: 'fixed',
          bottom: 100,
          right: 24,
          width: 60,
          height: 60,
          backgroundColor: isDark ? deepGreen : goldColor,
          color: isDark ? goldColor : deepGreen,
          zIndex: 1000,
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: isDark ? '#0F3B2E' : '#D4C589',
            transform: 'scale(1.1)',
          },
          '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
            '&:hover': {
              transform: 'none',
              backgroundColor: isDark ? deepGreen : goldColor,
            }
          },
          transition: 'all 0.3s ease',
        }}
      >
        <People sx={{ fontSize: 28 }} />
      </IconButton>

      {/* Floating Feedback Button */}
      <IconButton
        onClick={handleFeedbackClick}
        disabled={isLoading}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 60,
          height: 60,
          backgroundColor: isDark ? deepGreen : goldColor,
          color: isDark ? goldColor : deepGreen,
          zIndex: 1000,
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: isDark ? '#0F3B2E' : '#D4C589',
            transform: 'scale(1.1)',
          },
          '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
            '&:hover': {
              transform: 'none',
              backgroundColor: isDark ? deepGreen : goldColor,
            }
          },
          transition: 'all 0.3s ease',
        }}
      >
        <Feedback sx={{ fontSize: 28 }} />
      </IconButton>

      {/* Scroll to Top Button - Simple arrow at bottom center */}
      <Fade in={showScrollTop}>
        <IconButton
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 48,
            height: 48,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(10px)',
            color: goldColor,
            zIndex: 1000,
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
              transform: 'translateX(-50%) scale(1.1)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <KeyboardArrowUp sx={{ fontSize: 28 }} />
        </IconButton>
      </Fade>

      {/* Login Required Modal */}
      <LoginRequiredModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
      />
      
      {/* Community Chat Modal - Only show if user is authenticated */}
      {isAuthenticated && (
        <CommunityChatModal 
          open={chatOpen} 
          onClose={() => setChatOpen(false)} 
        />
      )}
      
      {/* Feedback Modal - Only show if user is authenticated */}
      {isAuthenticated && (
        <FeedbackModal 
          open={feedbackOpen} 
          onClose={() => setFeedbackOpen(false)} 
        />
      )}
      
      {/* 1. Hero Video Section */}
      <HeroSection isDark={isDark} />

      {/* 2. FAQ Section */}
      <FAQSection isDark={isDark} />

      {/* 3. Text Section (formerly HeroSection) */}
      <TextSection isDark={isDark} />

      {/* 4. State Boxes Section */}
      <Box 
        component="div"
        id="state-boxes-section"
        sx={{ 
          bgcolor: isDark ? '#000000' : '#FAFAFA',
          background: isDark 
            ? '#000000'
            : 'radial-gradient(circle at 50% 0%, rgba(196, 182, 111, 0.1) 0%, #FAFAFA 100%)',
        }}
      >
        <StateBoxesSection />
      </Box>

      {/* 5. Chat Section with full-width image */}
      <ChatSection isDark={isDark} />

      {/* 6. Testimonials Section */}
      <TestimonialsSection isDark={isDark} />

      {/* 7. Community Section with rotating images */}
      <CommunitySection isDark={isDark} />

      {/* 8. Drink Showcase Section (Local Layout Wrapper) */}
      <Box sx={{ pt:10, bgcolor: 'transparent' }}>
         <Container maxWidth="xl">
            <Typography 
               variant="h3" 
               align="center"
               sx={{ 
                 mb: 6, 
                 ...discordFont, 
                 color: cardTitleColor,
                 textTransform: 'uppercase'
               }}
            >
               What's your Escape while you work?
            </Typography>
            <DrinkShowcase />
         </Container>
      </Box>

      {/* 9. Partners Section */}
      <Partners isDark={isDark} />

      {/* 10. Coming Soon Section */}
      <Box 
        component="div"
        id="coming-soon-section"
        sx={{ 
          position: 'relative',
        }}
      >
        <ComingSoonSection isDark={isDark} />
      </Box>

      {/* 11. CTA Section */}
      <CTASection />

      {/* 12. Footer */}
      <Footer isDark={isDark} />
    </Box>
  );
};

export default LandingPage;